import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { merge, of as observableOf } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatListModule } from '@angular/material/list';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { ApiService } from '../../services/api/api.service';
import { TranslocoModule } from '@ngneat/transloco';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../common/confirm-dialog/confirn-dialog.component';
import { VulnerabilityAssessmentResultWithSeverityDTO } from '../../interfaces/VulnerabilityAssessmentResultWithSeverityDTO';
import { VulnerabilityAssessmentResultResponse } from '../../interfaces/VulnerabilityAssessmentResultResponse';
import { AlertDialogComponent } from '../../common/alert-dialog/alert-dialog.component';
import { VulnerabilityAssessmentResponse } from '../../interfaces/VulnerabilityAssessmentResponse';
import { VulnerabilityAssessment } from '../../interfaces/VulnerabilityAssessment';
import {
    FormsModule,
    ReactiveFormsModule,
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import { CompanyUser } from '../../interfaces/CompanyUser';
import { GlobalConstants } from '../../common/global-constants';
import { VulnerabilityManagerResponse } from '../../interfaces/VulnerabilityManagerResponse';
import * as XLSX from 'xlsx';

@Component({
    selector: 'app-v-view-vulnerabilities',
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        RouterLink,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        MatTooltipModule,
        TranslocoModule,
        MatProgressSpinnerModule,
        MatListModule,
    ],
    templateUrl: './v-view-vulnerabilities.component.html',
    styleUrl: './v-view-vulnerabilities.component.scss',
})
export class VViewVulnerabilitiesComponent {
    displayedColumns: string[] = [
        'name',
        'description',
        'impact',
        'ip',
        'severityLevel',
        'state',
        'action',
    ];
    data: VulnerabilityAssessmentResultWithSeverityDTO[] = [];
    dataSource =
        new MatTableDataSource<VulnerabilityAssessmentResultWithSeverityDTO>(
            this.data
        );
    totalItems = 0;
    pageSizes = [10, 20, 50, 1000];
    isLoadingResults = true;
    isRateLimitReached = false;

    // Assign Company User Form
    assignCompanyUserForm: FormGroup;

    // Array of company users
    companyUsersList: CompanyUser[] = [];

    selectedUser: string;

    // Vulnerability Assessment Id
    vulnerabilityAssessmentId: string = '';

    // Vulnerability Assessment Result Id
    vulnerabilityAssessmentResultId: string = '';

    // Vulnerability Assessment
    vulnerabilityAssessment: VulnerabilityAssessment;

    readonly dialog = inject(MatDialog);

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild('TABLE') table: ElementRef;

    // isToggled
    isToggled = false;

    internal = GlobalConstants.internal;
    supervisor = GlobalConstants.supervisor;

    constructor(
        public themeService: CustomizerSettingsService,
        private apiService: ApiService,
        private route: ActivatedRoute,
        private fb: FormBuilder
    ) {
        this.assignCompanyUserForm = this.fb.group({
            companyUser: ['', [Validators.required]],
        });
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for role name
     */
    get roleName(): string {
        return localStorage.getItem('roleName') ?? '';
    }

    /**
     * Getter for company name
     */
    get companyName(): string {
        return localStorage.getItem('companyName') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit() {
        this.route.params.subscribe((params) => {
            this.vulnerabilityAssessmentId =
                params['vulnerabilityAssessmentId'];
            this.updatedView();
        });
    }

    updatedView() {
        this.apiService
            .getVulnerabilityAssessmentById(
                this.vulnerabilityAssessmentId,
                this.roleName == GlobalConstants.internal
            )
            .subscribe((data: VulnerabilityAssessmentResponse) => {
                this.vulnerabilityAssessment = data.vulnerabilityAssessment;

                // If the user changes the sort order, reset back to the first page.
                this.sort.sortChange.subscribe(
                    () => (this.paginator.pageIndex = 0)
                );

                merge(this.sort.sortChange, this.paginator.page)
                    .pipe(
                        startWith({}),
                        switchMap(() => {
                            this.isLoadingResults = true;
                            return this.apiService
                                .getVulnerabilityAssessmentResultByAssesmentId(
                                    this.vulnerabilityAssessmentId,
                                    this.paginator.pageIndex,
                                    this.paginator.pageSize,
                                    this.sort.active,
                                    this.sort.direction,
                                    this.roleName !=
                                        GlobalConstants.internal
                                        ? false
                                        : true
                                )
                                .pipe(catchError(() => observableOf(null)));
                        }),
                        map((data) => {
                            // Flip flag to show that loading has finished.
                            this.isLoadingResults = false;
                            //this.isRateLimitReached = data === null;

                            if (data === null) {
                                return [];
                            }

                            // Only refresh the result length if there is new data. In case of rate
                            // limit errors, we do not want to reset the paginator to zero, as that
                            // would prevent users from re-triggering requests.
                            this.totalItems = data.totalElements;
                            return data.vulnerabilityAssessmentResultWithSeverityDTOS;
                        })
                    )
                    .subscribe((data) => (this.dataSource.data = data));

                this.dataSource.filterPredicate = function (
                    data,
                    filter: string
                ): boolean {
                    return (
                        data.vulnerabilityAssessmentResult.name
                            .toLowerCase()
                            .includes(filter) ||
                        (data.vulnerabilityAssessmentResult.description
                            ? data.vulnerabilityAssessmentResult.description
                            : ''
                        )
                            .toLowerCase()
                            .includes(filter) ||
                        (data.vulnerabilityAssessmentResult.impact
                            ? data.vulnerabilityAssessmentResult.impact
                            : ''
                        )
                            .toLowerCase()
                            .includes(filter) ||
                        (data.asset.ip ? data.asset.ip : '')
                            .toLowerCase()
                            .includes(filter) ||
                        data.severityLevelName
                            .toLowerCase()
                            .includes(filter) ||
                        (data.state.name ? data.state.name : '')
                            .toLowerCase()
                            .includes(filter)
                    );
                };
            });
    }

    // RTL Mode
    toggleRTLEnabledTheme() {
        this.themeService.toggleRTLEnabledTheme();
    }

    // Search Filter
    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    // onKeydown
    onKeydown(event: Event) {
        //console.log(event);
        return false;
    }

    // Popup Trigger
    classApplied = false;
    toggleClass() {
        this.classApplied = !this.classApplied;
    }

    // showCompanyUser
    showCompanyUser(id: string) {
        this.vulnerabilityAssessmentResultId = id;
        this.assignCompanyUserForm.reset();
        this.apiService
            .getCompanyUsersByVulnerabilityAssessmentId(
                this.vulnerabilityAssessmentId,
                this.roleName
            )
            .subscribe((response: VulnerabilityManagerResponse) => {
                this.companyUsersList = [];
                if (response.companyUsers.length > 0) {
                    this.companyUsersList.push(...response.companyUsers);

                    const vulnerability = this.dataSource.data.find(
                        ({ vulnerabilityAssessmentResult }) =>
                            vulnerabilityAssessmentResult.id ===
                            this.vulnerabilityAssessmentResultId
                    );
                    if (
                        vulnerability &&
                        vulnerability.vulnerabilityAssessmentResult.assignedUser
                    ) {
                        this.assignCompanyUserForm.setValue({
                            companyUser: [
                                vulnerability.vulnerabilityAssessmentResult
                                    .assignedUser.id,
                            ],
                        });
                    }
                }
                this.toggleClass();
            });
    }

    assignCompanyUser() {
        // Disable the form
        this.assignCompanyUserForm.disable();
        const assignUser = JSON.stringify({ userId: this.selectedUser[0] });
        this.apiService
            .patchVulnerabilityAssessmentResultAssignUser(
                this.vulnerabilityAssessmentResultId,
                assignUser
            )
            .subscribe({
                next: (value) => {
                    this.classApplied = !this.classApplied;
                    this.updatedView();
                },
                error: (error) => {
                    let errMsg: string = error;
                    const dialogRef = this.dialog.open(AlertDialogComponent, {
                        data: {
                            title: 'ERROR',
                            message: errMsg,
                        },
                    });
                },
                complete: () => {
                    this.assignCompanyUserForm.enable();
                },
            });
    }

    // Delete
    delete(id: string) {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                action: 'delete',
                component: 'vulnerability',
                content: 'content-delete',
                buttonText: {
                    ok: 'yes',
                    cancel: 'no',
                },
            },
        });

        dialogRef.afterClosed().subscribe((confirmed: boolean) => {
            if (confirmed) {
                console.log('Delete Vulnerability: ' + id);
                this.apiService
                    .deleteVulnerabilityAssessmentResult(id)
                    .subscribe({
                        next: (
                            value: VulnerabilityAssessmentResultResponse
                        ) => {
                            console.log(value);
                            this.updatedView();
                        },
                        error: (error) => {
                            let errMsg: string = error;
                            const dialogRef = this.dialog.open(
                                AlertDialogComponent,
                                {
                                    data: {
                                        title: 'ERROR',
                                        message: errMsg,
                                    },
                                }
                            );
                        },
                    });
            }
        });
    }

    exportTOExcel() {
        const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(
            this.table.nativeElement
        );
        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Vulnerabilities');

        /* save to file */
        XLSX.writeFile(wb, 'vulnerabilities.xlsx');
    }
}
